import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save } from "lucide-react";

export default function AdminCustomerRefer() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/merchant/config/customer-refer").then(({ data: d }) => {
    setData(d);
    setCfg(d.customer_refer);
  }).catch((e) => toast.error(extractErr(e)));

  useEffect(() => { load(); }, []);

  if (!data || !cfg) return <div className="text-slate-500">Loading…</div>;
  if (!data.active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Refer and Earn</strong> from the App Store to set rewards.</Card>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/merchant/config/customer-refer", cfg);
      setCfg(d.customer_refer);
      toast.success("Saved");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const review = async (id, status) => {
    try {
      await api.patch(`/merchant/customer-refer/withdrawals/${id}`, { status });
      toast.success(status === "paid" ? "Marked paid" : "Rejected and refunded");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Refer and Earn</h1>
        <p className="text-sm text-slate-500 mt-1">Direct referrals only. If A refers B, A earns. If B refers C, only B earns.</p>
      </div>
      <Card className="p-6 space-y-4 border-slate-200/70" data-testid="refer-settings">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Signup reward (₹)</Label><Input className="mt-1" type="number" data-testid="refer-signup-reward" value={cfg.signup_reward} onChange={(e) => setCfg({ ...cfg, signup_reward: e.target.value })} /></div>
          <div><Label>New product buying reward (₹)</Label><Input className="mt-1" type="number" data-testid="refer-purchase-reward" value={cfg.purchase_reward} onChange={(e) => setCfg({ ...cfg, purchase_reward: e.target.value })} /></div>
        </div>
        <div>
          <Label>Wallet can be used to</Label>
          <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="refer-wallet-mode" value={cfg.wallet_mode} onChange={(e) => setCfg({ ...cfg, wallet_mode: e.target.value })}>
            <option value="store_credit">Buy products only</option>
            <option value="withdraw">Withdraw only</option>
            <option value="both">Buy products or withdraw</option>
          </select>
        </div>
        {(cfg.wallet_mode === "withdraw" || cfg.wallet_mode === "both") && (
          <div><Label>Minimum withdrawal (₹)</Label><Input className="mt-1" type="number" data-testid="refer-min-withdraw" value={cfg.min_withdraw} onChange={(e) => setCfg({ ...cfg, min_withdraw: e.target.value })} /></div>
        )}
        <Button onClick={save} disabled={saving} data-testid="refer-save" className="bg-violet-600 hover:bg-violet-700"><Save className="w-4 h-4 mr-1" /> Save rewards</Button>
      </Card>
      <Card className="p-6 space-y-3 border-slate-200/70">
        <div className="font-semibold">Withdrawal requests</div>
        {(data.withdrawals || []).length === 0 ? (
          <p className="text-sm text-slate-500">No requests yet.</p>
        ) : (data.withdrawals || []).map((w) => (
          <div key={w.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-slate-200" data-testid={`withdraw-${w.id}`}>
            <div className="text-sm">
              <div className="font-mono-nums font-semibold">₹{w.amount}</div>
              <div className="text-xs text-slate-500">{w.payout?.method === "upi" ? `UPI ${w.payout?.upi}` : `${w.payout?.account_name} · ${w.payout?.account_number}`}</div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="capitalize">{w.status}</Badge>
              {w.status === "pending" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => review(w.id, "paid")}>Mark paid</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => review(w.id, "rejected")}>Reject</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
